import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Seo from '@/components/Seo';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { serviceDetails, getServiceBySlug } from '@/data/services';

const SITE = 'https://www.sr-zerspanung.de';

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  if (!service) return <Navigate to="/leistungen" replace />;

  const others = serviceDetails.filter((s) => s.slug !== service.slug);
  const path = `/leistungen/${service.slug}`;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Seo
        title={service.metaTitle}
        description={service.metaDescription}
        path={path}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: service.name,
            serviceType: service.name,
            description: service.metaDescription,
            url: `${SITE}${path}`,
            areaServed: ['Oberpfalz', 'Landkreis Cham', 'Regensburg', 'Deutschland'],
            provider: {
              '@type': 'LocalBusiness',
              name: 'SR-Zerspanung',
              url: SITE,
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Stamsried',
                addressRegion: 'Bayern',
                addressCountry: 'DE',
              },
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Startseite', item: `${SITE}/` },
              { '@type': 'ListItem', position: 2, name: 'Leistungen', item: `${SITE}/leistungen` },
              { '@type': 'ListItem', position: 3, name: service.name, item: `${SITE}${path}` },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: service.faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
          },
        ]}
      />
      <Header />

      <main className="flex-1 pt-28 md:pt-32 lg:pt-24">
        <section className="py-16 lg:py-24 bg-gradient-steel">
          <div className="container mx-auto px-4">
            <nav aria-label="Brotkrumen" className="mb-8 text-sm text-muted-foreground">
              <Link to="/leistungen" className="inline-flex items-center gap-2 hover:text-foreground transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Alle Leistungen
              </Link>
            </nav>
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <p className="text-primary font-semibold uppercase tracking-widest text-sm mb-4">Leistung</p>
                <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
                  {service.title}
                </h1>
                <p className="text-muted-foreground text-lg leading-relaxed mb-8">{service.intro}</p>
                <Button size="lg" className="gap-2" asChild>
                  <Link to="/anfrage">
                    <Zap className="w-4 h-4" />
                    Jetzt anfragen
                  </Link>
                </Button>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="rounded-2xl overflow-hidden shadow-xl"
              >
                <img src={service.image} alt={service.imageAlt} loading="eager" className="w-full h-full object-cover" />
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-12 border-b border-border">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {service.highlights.map((h) => (
                <div key={h.label} className="rounded-xl border border-border bg-card p-5 h-full">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{h.label}</p>
                  <p className="font-heading font-bold text-lg text-foreground">{h.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4 max-w-4xl space-y-12">
            {service.sections.map((sec) => (
              <article key={sec.heading}>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-4">{sec.heading}</h2>
                <p className="text-muted-foreground leading-relaxed">{sec.body}</p>
                {sec.bullets && (
                  <ul className="mt-5 space-y-2">
                    {sec.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-foreground">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            ))}

            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-6">Häufige Fragen</h2>
              <div className="space-y-6">
                {service.faqs.map((faq) => (
                  <div key={faq.question}>
                    <h3 className="font-semibold text-foreground mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/40">
          <div className="container mx-auto px-4">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-8">Weitere Leistungen</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/leistungen/${o.slug}`}
                  className="group rounded-xl border border-border bg-card p-6 h-full flex flex-col justify-between hover:border-primary transition-colors"
                >
                  <div>
                    <h3 className="font-heading font-bold text-lg text-foreground mb-2">{o.name}</h3>
                    <p className="text-muted-foreground text-sm">{o.intro.slice(0, 120)}…</p>
                  </div>
                  <span className="mt-4 inline-flex items-center gap-2 text-primary text-sm font-semibold">
                    Mehr erfahren
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServiceDetail;
